(function () {
  'use strict';

  function initializePlanner(root) {
    const core = window.MSProjectPlannerCore;
    const geometry = window.MSProjectPlannerGeometry;
    const rules = window.MSProjectPlannerRules;
    const config = window.MSProjectPlannerConfig;

    if (!core || !geometry || !rules || !config) {
      root.setAttribute(
        'data-ms-project-planner-status',
        'unavailable'
      );
      return;
    }

    const materialValidation =
      rules.validateMaterialCollection(config.materials);

    if (materialValidation.status !== rules.STATUS.VALID) {
      root.setAttribute(
        'data-ms-project-planner-status',
        'invalid-material-configuration'
      );
      return;
    }

    const projectNameInput = root.querySelector(
      '[data-ms-project-name]'
    );
    const areaList = root.querySelector(
      '[data-ms-area-list]'
    );
    const areaTemplate = root.querySelector(
      '[data-ms-area-template]'
    );
    const addAreaButton = root.querySelector(
      '[data-ms-add-area]'
    );
    const summaryStatus = root.querySelector(
      '[data-ms-summary-status]'
    );
    const summaryResults = root.querySelector(
      '[data-ms-summary-results]'
    );
    const summaryWeightDisclaimer = root.querySelector(
      '[data-ms-summary-weight-disclaimer]'
    );

    if (
      !projectNameInput ||
      !areaList ||
      !areaTemplate ||
      !addAreaButton ||
      !summaryStatus ||
      !summaryResults
    ) {
      root.setAttribute(
        'data-ms-project-planner-status',
        'invalid-shell'
      );
      return;
    }

    let state = core.createProjectState();
    // Compare customer inputs with core defaults, excluding generated IDs and UI state.
    const blankAreaInputs = JSON.stringify(state.areas[0].inputs);
    let pendingDeleteAreaId = null;
    const expandedAreaIds = new Set();
    let revision = null;
    let recovery = null;
    let lastSavedSignature = null;
    try {
      if (window.MSProjectPlannerPersistence && window.localStorage) {
        recovery = window.MSProjectPlannerPersistence.create(window.localStorage, core);
        const restored = recovery.load();
        if (restored) {
          state = restored.state;
          revision = restored.revision;
          lastSavedSignature = JSON.stringify({state, revision});
          const notice = root.querySelector('[data-ms-restored-notice]');
          if (notice) notice.hidden = false;
          if (revision) addAreaButton.disabled = true;
        }
      }
    } catch (_) { /* Private browsing or blocked storage must not stop planning. */ }

    function saveRecovery(beforeNavigation = false) {
      const isBlank = !revision && state.project.name === '' && state.areas.length === 1 &&
        state.areas[0].name === '' && JSON.stringify(state.areas[0].inputs) === blankAreaInputs;
      if (!recovery) return isBlank;
      const signature = JSON.stringify({state, revision});
      if (!beforeNavigation && signature === lastSavedSignature) return true;
      // Re-save even an unchanged project before POST; storage may have failed or changed.
      if (isBlank && !beforeNavigation) recovery.clear();
      else if (!recovery.save(state, revision)) return false;
      lastSavedSignature = signature;
      return true;
    }

    function replaceArea(areaId, area) {
      state = {...state, areas: state.areas.map(function (current) {
        return current.id === areaId ? area : current;
      })};
    }

    const materialLabels = new Map(
      config.materials.map(function (material) {
        return [material.id, material.label];
      })
    );

    function formatMeasurement(value) {
      if (!Number.isFinite(value)) {
        return '';
      }

      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2
      }).format(value);
    }

    function getArea(areaId) {
      return state.areas.find(function (area) {
        return area.id === areaId;
      });
    }

    function getMethodInputs(area) {
      const method = area.inputs.measurementMethod;

      if (!method) {
        return {};
      }

      return (
        area.inputs.measurementInputsByMethod[method] || {}
      );
    }

    function resolveExistingMaterialState(area) {
      const materialId =
        area &&
        area.inputs &&
        typeof area.inputs.materialId === 'string' &&
        area.inputs.materialId.trim() !== ''
          ? area.inputs.materialId.trim()
          : null;

      if (!materialId) {
        return {
          status: 'none',
          materialId: null,
          label: null,
          blocked: false
        };
      }

      const material =
        config.materials.find(
          function (candidate) {
            return candidate.id === materialId;
          }
        );

      if (!material) {
        return {
          status: 'blocked-missing-config',
          materialId: materialId,
          label: null,
          blocked: true
        };
      }

      if (
        material.availability ===
        'not-offered-in-planner'
      ) {
        return {
          status: 'blocked-not-offered',
          materialId: materialId,
          label: material.label,
          blocked: true
        };
      }

      return {
        status: 'active',
        materialId: materialId,
        label: material.label,
        availability: material.availability,
        blocked: false
      };
    }

    // SMALLER_PARTS_FUNCTIONAL_V1
    function deriveAreaMeasurement(area) {
      if (
        area &&
        area.inputs &&
        area.inputs.measurementComposition ===
          'smaller-parts'
      ) {
        return rules.deriveSmallerPartsMeasurement(
          Array.isArray(
            area.inputs.measurementParts
          )
            ? area.inputs.measurementParts
            : [],
          geometry.deriveMeasurement
        );
      }

      const inputs = getMethodInputs(area);
      if (area.inputs.measurementMethod === 'round-border') {
        const overall = geometry.normalizeLinearDimension(inputs.overallDiameter);
        const feature = geometry.normalizeLinearDimension(inputs.featureDiameter);
        const invalid = overall.status === geometry.STATUS.INVALID || feature.status === geometry.STATUS.INVALID;
        const complete = overall.status === geometry.STATUS.VALID && feature.status === geometry.STATUS.VALID;
        return geometry.deriveRoundBorder({
          insideDiameter: invalid ? {feet: -1} : complete ? {feet: feature.feet} : {},
          borderWidth: invalid ? {feet: -1} : complete ? {feet: (overall.feet - feature.feet) / 2} : {}
        });
      }
      return geometry.deriveMeasurement(area.inputs.measurementMethod, inputs);
    }

    function deriveAreaBundle(area) {
      const geometryResult =
        deriveAreaMeasurement(area);
      const derived = core.deriveArea(
        area,
        geometryResult
      );
      const materialState =
        resolveExistingMaterialState(area);
      const rawContribution =
        core.deriveAreaContribution(
          area,
          geometryResult
        );

      const purchaseMethod = rules.resolvePurchaseMethod(getSelectableMaterial(area.inputs.materialId), area.inputs.purchaseMethod);
      const contribution =
        (materialState.blocked || !purchaseMethod) &&
        rawContribution.status ===
          core.MEASUREMENT_STATUS.VALID
          ? Object.freeze({
              ...rawContribution,
              status:
                core.MEASUREMENT_STATUS.INCOMPLETE,
              cubicYards: null
            })
          : {...rawContribution, purchaseMethod};

      return {
        geometryResult: geometryResult,
        derived: derived,
        contribution: contribution,
        materialState: materialState
      };
    }

    function setInputValue(input, value) {
      const nextValue =
        value === null || value === undefined
          ? ''
          : String(value);

      if (input.value !== nextValue) {
        input.value = nextValue;
      }
    }

    function populateMaterialOptions(card) {
      const select = card.querySelector(
        '[data-ms-material]'
      );

      if (!select) {
        return;
      }

      config.materials.forEach(function (material) {
        const eligibility =
          rules.evaluateMaterialSelectionEligibility(
            material
          );

        if (!eligibility.selectable) {
          return;
        }

        const option = document.createElement('option');
        option.value = material.id;
        option.textContent = material.selectorLabel || material.label;
        select.appendChild(option);
      });
    }

    function getSelectableMaterial(materialId) {
      if (!materialId) {
        return null;
      }

      const material =
        config.materials.find(
          function (candidate) {
            return candidate.id === materialId;
          }
        );

      if (!material) {
        return null;
      }

      const eligibility =
        rules.evaluateMaterialSelectionEligibility(
          material
        );

      return eligibility.selectable
        ? material
        : null;
    }

    function getMatchingDepthPreset(
      material,
      depthValue
    ) {
      if (
        !material ||
        !Array.isArray(material.depthPresets) ||
        depthValue === null ||
        depthValue === undefined ||
        String(depthValue).trim() === ''
      ) {
        return null;
      }

      const numericDepth = Number(depthValue);

      if (
        !Number.isFinite(numericDepth) ||
        !Number.isInteger(numericDepth) ||
        numericDepth <= 0
      ) {
        return null;
      }

      const matchingPreset =
        material.depthPresets.find(
          function (preset) {
            return preset === numericDepth;
          }
        );

      return matchingPreset === undefined
        ? null
        : matchingPreset;
    }

    function renderDepthChoices(card, area) {
      const choiceInput = card.querySelector(
        '[data-ms-depth-choice]'
      );
      const depthInput = card.querySelector(
        '[data-ms-depth-inches]'
      );
      const customDepth = card.querySelector(
        '[data-ms-custom-depth]'
      );

      if (!choiceInput) {
        return;
      }

      const material = getSelectableMaterial(
        area.inputs.materialId
      );

      choiceInput.replaceChildren();

      const blankOption =
        document.createElement('option');
      blankOption.value = '';
      blankOption.textContent =
        'How many inches deep do you want your material?';
      choiceInput.appendChild(blankOption);

      if (
        material &&
        Array.isArray(material.depthPresets)
      ) {
        material.depthPresets.forEach(
          function (preset) {
            const option =
              document.createElement('option');

            option.value = String(preset);
            option.textContent =
              String(preset) + ' inches';

            choiceInput.appendChild(option);
          }
        );
      }

      const customOption =
        document.createElement('option');
      customOption.value = 'custom';
      customOption.textContent =
        'Custom';
      choiceInput.appendChild(customOption);

      const matchingPreset =
        getMatchingDepthPreset(
          material,
          area.inputs.depthInches
        );

      let selectedChoice = '';

      const depthValue =
        area.inputs.depthInches;
      const depthIsBlank =
        depthValue === null ||
        depthValue === undefined ||
        String(depthValue).trim() === '';
      const customModeActive =
        card.getAttribute(
          'data-ms-custom-depth-active'
        ) === 'true';

      if (!depthIsBlank) {
        selectedChoice =
          matchingPreset === null
            ? 'custom'
            : String(matchingPreset);
      } else if (customModeActive) {
        selectedChoice = 'custom';
      }

      setInputValue(
        choiceInput,
        selectedChoice
      );

      if (depthInput) {
        setInputValue(
          depthInput,
          area.inputs.depthInches
        );
      }

      if (customDepth) {
        customDepth.hidden =
          selectedChoice !== 'custom';
      }

      const depthError = card.querySelector(
        '[data-ms-depth-error]'
      );
      const numericDepth = Number(depthValue);
      const invalidCustomDepth =
        selectedChoice === 'custom' &&
        !depthIsBlank &&
        (
          !Number.isFinite(numericDepth) ||
          !Number.isInteger(numericDepth) ||
          numericDepth <= 0
        );

      if (depthInput) {
        depthInput.setAttribute(
          'aria-invalid',
          invalidCustomDepth ? 'true' : 'false'
        );
      }

      if (depthError) {
        depthError.hidden = !invalidCustomDepth;
      }
    }

    function showCustomDepthEntry(card) {
      const customDepth = card.querySelector(
        '[data-ms-custom-depth]'
      );
      const depthInput = card.querySelector(
        '[data-ms-depth-inches]'
      );

      if (customDepth) {
        customDepth.hidden = false;
      }

      if (
        depthInput &&
        typeof depthInput.focus === 'function'
      ) {
        depthInput.focus();
      }
    }

    function assignAccessibleIds(card, areaId) {
      const methodInput = card.querySelector(
        '[data-ms-measurement-method]'
      );
      const depthInput = card.querySelector(
        '[data-ms-depth-inches]'
      );
      const depthError = card.querySelector(
        '[data-ms-depth-error]'
      );

      if (methodInput) {
        methodInput.id =
          'ms-project-planner-method-' + areaId;

        const field = methodInput.closest(
          '.ms-project-planner__field'
        );
        const label = field
          ? field.querySelector('label')
          : null;

        if (label) {
          label.htmlFor = methodInput.id;
        }
      }

      if (depthInput) {
        depthInput.id =
          'ms-project-planner-depth-' + areaId;

        if (depthError) {
          depthError.id =
            'ms-project-planner-depth-error-' + areaId;
          depthInput.setAttribute(
            'aria-describedby',
            depthError.id
          );
        }

        const field = depthInput.closest(
          '.ms-project-planner__field'
        );
        const label = field
          ? field.querySelector('label')
          : null;

        if (label) {
          label.htmlFor = depthInput.id;
        }
      }

      const knownInput = card.querySelector(
        '[data-ms-method="known-square-footage"][data-ms-field="squareFeet"]'
      );

      if (knownInput) {
        knownInput.id =
          'ms-project-planner-known-square-feet-' +
          areaId;

        const field = knownInput.closest(
          '.ms-project-planner__field'
        );
        const label = field
          ? field.querySelector('label')
          : null;

        if (label) {
          label.htmlFor = knownInput.id;
        }
      }
    }

    function createAreaCard(area) {
      const fragment =
        areaTemplate.content.cloneNode(true);
      const card = fragment.querySelector(
        '[data-ms-area-card]'
      );

      if (!card) {
        throw new Error(
          'Area template does not contain an area card.'
        );
      }

      card.setAttribute(
        'data-ms-area-id',
        area.id
      );

      populateMaterialOptions(card);
      assignAccessibleIds(card, area.id);
      const details = card.querySelector('[data-ms-area-details]');
      const toggle = card.querySelector('[data-ms-toggle-area]');
      if (details && toggle) {
        details.id = 'ms-project-planner-details-' + area.id;
        toggle.setAttribute('aria-controls', details.id);
      }

      return card;
    }

    function getGeometryInputValue(area, input) {
      const method = input.getAttribute(
        'data-ms-method'
      );
      const field = input.getAttribute(
        'data-ms-field'
      );
      const unit = input.getAttribute(
        'data-ms-unit'
      );

      if (!method || !field) {
        return '';
      }

      const methodInputs =
        area.inputs.measurementInputsByMethod[
          method
        ] || {};

      const storedValue = methodInputs[field];

      if (unit) {
        if (
          storedValue &&
          typeof storedValue === 'object'
        ) {
          return storedValue[unit];
        }

        return '';
      }

      return storedValue;
    }

    function hidePerimeterFacts(card) {
      const rows = [
        [
          '[data-ms-perimeter-total-row]',
          '[data-ms-perimeter-total]'
        ],
        [
          '[data-ms-perimeter-inside-row]',
          '[data-ms-perimeter-inside]'
        ],
        [
          '[data-ms-perimeter-outside-row]',
          '[data-ms-perimeter-outside]'
        ]
      ];

      rows.forEach(function (selectors) {
        const row = card.querySelector(
          selectors[0]
        );
        const value = card.querySelector(
          selectors[1]
        );

        if (row) {
          row.hidden = true;
        }

        if (value) {
          value.textContent = '';
        }
      });
    }

    function renderMeasurementFacts(
      card,
      measurement
    ) {
      const facts = card.querySelector(
        '[data-ms-measurement-facts]'
      );
      const squareFeet = card.querySelector(
        '[data-ms-square-feet-result]'
      );

      if (!facts || !squareFeet) {
        return;
      }

      if (
        !measurement ||
        measurement.status !==
          core.MEASUREMENT_STATUS.VALID
      ) {
        facts.hidden = true;
        squareFeet.textContent = '';
        hidePerimeterFacts(card);
        return;
      }

      facts.hidden = false;
      squareFeet.textContent = formatMeasurement(
        measurement.squareFeet
      );

      hidePerimeterFacts(card);

      const mappings = [
        [
          'totalFeet',
          '[data-ms-perimeter-total-row]',
          '[data-ms-perimeter-total]'
        ],
        [
          'insideFeet',
          '[data-ms-perimeter-inside-row]',
          '[data-ms-perimeter-inside]'
        ],
        [
          'outsideFeet',
          '[data-ms-perimeter-outside-row]',
          '[data-ms-perimeter-outside]'
        ]
      ];

      mappings.forEach(function (mapping) {
        if (
          Object.prototype.hasOwnProperty.call(
            measurement.perimeterFacts,
            mapping[0]
          )
        ) {
          const row = card.querySelector(
            mapping[1]
          );
          const value = card.querySelector(
            mapping[2]
          );

          if (value) {
            value.textContent = formatMeasurement(
              measurement.perimeterFacts[
                mapping[0]
              ]
            );
          }

          if (row) {
            row.hidden = false;
          }
        }
      });
    }


    // SMALLER_PARTS_FUNCTIONAL_V1
    let smallerPartSequence = 0;

    function getMeasurementParts(area) {
      if (
        !area ||
        !area.inputs ||
        !Array.isArray(
          area.inputs.measurementParts
        )
      ) {
        return [];
      }

      return area.inputs.measurementParts;
    }

    function createMeasurementPart() {
      smallerPartSequence += 1;

      return {
        id:
          'part-' +
          Date.now().toString(36) +
          '-' +
          smallerPartSequence.toString(36),
        measurementMethod: null,
        measurementInputsByMethod: {
          rectangle: {},
          'round-area': {},
          triangle: {}
        }
      };
    }

    function updateSmallerPartsArea(
      areaId,
      patch,
      preservePartControls
    ) {
      state = core.updateAreaInputs(
        state,
        areaId,
        patch
      );
      if (patch.measurementComposition === 'smaller-parts') {
        const related = getArea(areaId).inputs.relatedMaterials || {};
        state = core.updateAreaInputs(state, areaId, {relatedMaterials: {
          ...related, alphaEdge: {...(related.alphaEdge || {}), extent: null,
            cornersInclude: null, ninetyDegreeConnectorsPerArea: null,
            lengthSides: null, widthSides: null, squareSides: null}
        }});
      }

      rerenderArea(areaId, preservePartControls);
    }

    function beginSmallerPartsMeasurement(
      areaId
    ) {
      const area = getArea(areaId);

      if (!area) {
        return;
      }

      const existingParts =
        getMeasurementParts(area);

      updateSmallerPartsArea(
        areaId,
        {
          measurementComposition:
            'smaller-parts',
          measurementMethod: null,
          measurementGuidanceStep: null,
          measurementParts:
            existingParts.length > 0
              ? existingParts
              : [createMeasurementPart()],
          measurementPartsDone: false
        }
      );
    }

    function addSmallerMeasurementPart(
      areaId
    ) {
      const area = getArea(areaId);

      if (!area) {
        return;
      }

      updateSmallerPartsArea(
        areaId,
        {
          measurementComposition:
            'smaller-parts',
          measurementParts:
            getMeasurementParts(area).concat(
              createMeasurementPart()
            ),
          measurementPartsDone: false
        }
      );
    }

    function updateSmallerPartMethod(
      areaId,
      partId,
      method
    ) {
      if (
        !rules.SMALLER_PART_METHODS.includes(
          method
        )
      ) {
        return;
      }

      const area = getArea(areaId);

      if (!area) {
        return;
      }

      const parts =
        getMeasurementParts(area).map(
          function (part) {
            if (part.id !== partId) {
              return part;
            }

            return {
              ...part,
              measurementMethod: method,
              measurementInputsByMethod: {
                rectangle: {},
                'round-area': {},
                triangle: {},
                ...(
                  part.measurementInputsByMethod ||
                  {}
                )
              }
            };
          }
        );

      updateSmallerPartsArea(
        areaId,
        {
          measurementComposition:
            'smaller-parts',
          measurementParts: parts,
          measurementPartsDone: false
        },
        true
      );
    }

    function updateSmallerPartGeometryInput(
      areaId,
      partId,
      input
    ) {
      const area = getArea(areaId);

      if (!area) {
        return;
      }

      const method =
        input.getAttribute(
          'data-ms-part-method-name'
        );

      const field =
        input.getAttribute(
          'data-ms-part-field'
        );

      const unit =
        input.getAttribute(
          'data-ms-part-unit'
        );

      if (
        !rules.SMALLER_PART_METHODS.includes(
          method
        ) ||
        !field
      ) {
        return;
      }

      const parts =
        getMeasurementParts(area).map(
          function (part) {
            if (part.id !== partId) {
              return part;
            }

            const byMethod =
              part.measurementInputsByMethod ||
              {};

            const existing =
              byMethod[method] || {};

            let next;

            if (unit) {
              next = {
                ...existing,
                [field]: {
                  ...(existing[field] || {}),
                  [unit]: input.value
                }
              };
            } else {
              next = {
                ...existing,
                [field]: input.value
              };
            }

            return {
              ...part,
              measurementInputsByMethod: {
                ...byMethod,
                [method]: next
              }
            };
          }
        );

      updateSmallerPartsArea(
        areaId,
        {
          measurementComposition:
            'smaller-parts',
          measurementParts: parts,
          measurementPartsDone: false
        },
        true
      );
    }

    function removeSmallerMeasurementPart(areaId, partId) {
      const area = getArea(areaId);
      const parts = getMeasurementParts(area);
      if (parts.length < 2 || !parts.some(part => part.id === partId)) return;
      updateSmallerPartsArea(areaId, {
        measurementParts: parts.filter(part => part.id !== partId),
        measurementPartsDone: false
      });
    }

    function renderIntermediateSmallerPartsFacts(card, area) {
      const validParts = getMeasurementParts(area).filter(function (part) {
        const measurement = geometry.deriveMeasurement(
          part.measurementMethod,
          (part.measurementInputsByMethod || {})[part.measurementMethod] || {}
        );
        return measurement && measurement.status === rules.STATUS.VALID;
      });
      renderMeasurementFacts(card, rules.deriveSmallerPartsMeasurement(
        validParts, geometry.deriveMeasurement
      ));
    }

    function deriveSmallerPartsForArea(area) {
      return rules.deriveSmallerPartsMeasurement(
        getMeasurementParts(area),
        geometry.deriveMeasurement
      );
    }

    function smallerPartsAreValid(area) {
      return (
        deriveSmallerPartsForArea(area)
          .status === rules.STATUS.VALID
      );
    }

    function appendSmallerPartInput(
      container,
      part,
      labelText,
      field,
      unit
    ) {
      const label =
        document.createElement('label');

      label.className =
        'ms-project-planner__dimension-field';

      const caption =
        document.createElement('span');

      caption.textContent = labelText;
      label.appendChild(caption);

      const input =
        document.createElement('input');

      input.type = 'number';
      input.inputMode = 'decimal';
      input.min = '0';
      input.step = 'any';
      input.className =
        'ms-project-planner__input';

      input.setAttribute(
        'data-ms-smaller-part-input',
        ''
      );

      input.setAttribute(
        'data-ms-part-method-name',
        part.measurementMethod
      );

      input.setAttribute(
        'data-ms-part-field',
        field
      );

      if (unit) {
        input.setAttribute(
          'data-ms-part-unit',
          unit
        );
      }

      const byMethod =
        part.measurementInputsByMethod ||
        {};

      const current =
        byMethod[part.measurementMethod] ||
        {};

      let value = current[field];

      if (
        unit &&
        value &&
        typeof value === 'object'
      ) {
        value = value[unit];
      }

      input.value =
        value === null ||
        value === undefined
          ? ''
          : String(value);

      label.appendChild(input);
      container.appendChild(label);
    }

    function appendFeetAndInchesInputs(
      container,
      part,
      prompt,
      field
    ) {
      const group =
        document.createElement('div');

      group.className =
        'ms-project-planner__dimension';

      const heading =
        document.createElement('p');

      heading.className =
        'ms-project-planner__label';

      heading.textContent = prompt;
      group.appendChild(heading);

      const inputs =
        document.createElement('div');

      inputs.className =
        'ms-project-planner__dimension-inputs';

      appendSmallerPartInput(
        inputs,
        part,
        'Feet',
        field,
        'feet'
      );

      appendSmallerPartInput(
        inputs,
        part,
        'Inches',
        field,
        'inches'
      );

      group.appendChild(inputs);
      container.appendChild(group);
    }

    function renderSmallerMeasurementPart(
      host,
      area,
      part,
      index,
      existingSection
    ) {
      const section =
        existingSection || document.createElement('section');

      section.className =
        'ms-project-planner__smaller-part';

      section.setAttribute(
        'data-ms-smaller-part',
        part.id
      );

      if (!existingSection) {
      const heading =
        document.createElement('h4');

      heading.textContent =
        'Section ' + (index + 1);

      section.appendChild(heading);

      const question =
        document.createElement('p');

      question.className =
        'ms-project-planner__label';

      question.textContent =
        'Which description best matches this section?';

      section.appendChild(question);

      [
        [
          'rectangle',
          'Square or rectangular'
        ],
        [
          'round-area',
          'Round'
        ],
        [
          'triangle',
          'Three-sided'
        ]
      ].forEach(function (choice) {
        const label =
          document.createElement('label');

        const input =
          document.createElement('input');

        input.type = 'radio';
        input.name =
          'ms-smaller-part-method-' +
          area.id +
          '-' +
          part.id;

        input.value = choice[0];

        input.checked =
          part.measurementMethod ===
          choice[0];

        input.setAttribute(
          'data-ms-smaller-part-method',
          ''
        );

        label.appendChild(input);
        label.appendChild(
          document.createTextNode(
            ' ' + choice[1]
          )
        );

        section.appendChild(label);
      });

      }

      let measurements = section.querySelector('[data-ms-part-measurements]');
      if (!measurements) {
        measurements = document.createElement('div');
        measurements.setAttribute('data-ms-part-measurements', '');
        section.appendChild(measurements);
      }
      measurements.replaceChildren();
      section.setAttribute('data-ms-rendered-method', part.measurementMethod || '');
      if (
        part.measurementMethod ===
        'rectangle'
      ) {
        appendFeetAndInchesInputs(
          measurements,
          part,
          'How long is this section?',
          'length'
        );

        appendFeetAndInchesInputs(
          measurements,
          part,
          'How wide is this section?',
          'width'
        );
      } else if (
        part.measurementMethod ===
        'round-area'
      ) {
        const help =
          document.createElement('p');

        help.className =
          'ms-project-planner__help';

        help.textContent =
          'Measure across the middle of this section';

        measurements.appendChild(help);

        appendFeetAndInchesInputs(
          measurements,
          part,
          'Across the middle',
          'diameter'
        );
      } else if (
        part.measurementMethod ===
        'triangle'
      ) {
        const help = document.createElement('p');
        help.className = 'ms-project-planner__help';
        help.textContent = 'Measure straight from one corner to the next—not along a curved edge.';
        measurements.appendChild(help);
        [1, 2, 3].forEach(number => {
          appendFeetAndInchesInputs(measurements, part, 'Side ' + number, 'side' + number);
        });
        const error = document.createElement('p');
        error.setAttribute('data-ms-triangle-error', '');
        error.setAttribute('role', 'status');
        error.hidden = true;
        error.textContent = 'These three side measurements don’t work together. Please recheck Side 1, Side 2, and Side 3.';
        measurements.appendChild(error);
      }

      if (!existingSection && getMeasurementParts(area).length > 1) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'button button--secondary';
        remove.setAttribute('data-ms-remove-smaller-part', part.id);
        remove.textContent = 'Remove Section ' + (index + 1);
        section.appendChild(remove);
      }
      renderSmallerPartResult(section, part, index);
      if (!existingSection) host.appendChild(section);
    }

    function renderSmallerPartResult(section, part, index) {
      const existingResult = section.querySelector('[data-ms-part-square-feet]');
      if (existingResult) existingResult.hidden = true;
      if (part.measurementMethod) {
        const byMethod =
          part.measurementInputsByMethod ||
          {};

        const measurement =
          geometry.deriveMeasurement(
            part.measurementMethod,
            byMethod[
              part.measurementMethod
            ] || {}
          );

        const error = section.querySelector('[data-ms-triangle-error]');
        if (error) error.hidden = measurement.status !== rules.STATUS.INVALID;

        if (
          measurement &&
          measurement.status ===
            rules.STATUS.VALID
        ) {
          const result = existingResult ||
            document.createElement('p');
          result.hidden = false;

          result.setAttribute(
            'data-ms-part-square-feet',
            ''
          );

          result.textContent =
            'Section ' +
            (index + 1) +
            ': ' +
            formatMeasurement(
              measurement.squareFeet
            ) +
            ' square feet';

          if (!existingResult) section.appendChild(result);
        }
      }

    }

    function smallerPartsWorkflowIsComplete(area) {
      if (
        !area ||
        !area.inputs ||
        area.inputs.measurementComposition !==
          'smaller-parts'
      ) {
        return true;
      }

      return (
        deriveSmallerPartsForArea(area).status ===
          rules.STATUS.VALID &&
        area.inputs.measurementPartsDone === true
      );
    }

    function renderSmallerParts(
      card,
      area,
      preservePartControls
    ) {
      const host =
        card.querySelector(
          '[data-ms-smaller-parts-host]'
        );

      const beginButton =
        card.querySelector(
          '[data-ms-measure-first-part]'
        );

      const active =
        area.inputs
          .measurementComposition ===
        'smaller-parts';

      if (beginButton) {
        beginButton.hidden = active;
      }

      if (!host) {
        return;
      }

      host.hidden = !active;
      if (!preservePartControls || !active) host.replaceChildren();

      if (!active) {
        return;
      }

      const parts =
        getMeasurementParts(area);

      parts.forEach(
        function (part, index) {
          if (preservePartControls) {
            const section = host.querySelector('[data-ms-smaller-part="' + part.id + '"]');
            if (section.getAttribute('data-ms-rendered-method') !== (part.measurementMethod || '')) {
              renderSmallerMeasurementPart(host, area, part, index, section);
            } else {
              renderSmallerPartResult(section, part, index);
            }
          } else {
            renderSmallerMeasurementPart(host, area, part, index);
          }
        }
      );

      const valid =
        smallerPartsAreValid(area);

      const existingActions = host.querySelector('[data-ms-smaller-parts-actions]');
      if (existingActions) {
        existingActions.hidden = !valid;
        return;
      }
      if (!valid) {
        return;
      }

      const actions =
        document.createElement('div');

      actions.setAttribute('data-ms-smaller-parts-actions', '');
      actions.className =
        'ms-project-planner__area-actions';

      const addButton =
        document.createElement('button');

      addButton.type = 'button';
      addButton.className =
        'button button--secondary';

      addButton.setAttribute(
        'data-ms-add-smaller-part',
        ''
      );

      addButton.textContent =
        '+ Add another section';

      actions.appendChild(addButton);

      const doneButton =
        document.createElement('button');

      doneButton.type = 'button';
      doneButton.className = 'button';

      doneButton.setAttribute(
        'data-ms-done-smaller-parts',
        ''
      );

      doneButton.textContent = 'Done adding sections';

      actions.appendChild(doneButton);
      host.appendChild(actions);
    }

    function handleSmallerPartInput(
      card,
      areaId,
      input
    ) {
      const partElement =
        input.closest(
          '[data-ms-smaller-part]'
        );

      if (!partElement) {
        return false;
      }

      const partId =
        partElement.getAttribute(
          'data-ms-smaller-part'
        );

      if (!partId) {
        return true;
      }

      if (
        input.matches(
          '[data-ms-smaller-part-method]'
        )
      ) {
        updateSmallerPartMethod(
          areaId,
          partId,
          input.value
        );

        return true;
      }

      if (
        input.matches(
          '[data-ms-smaller-part-input]'
        )
      ) {
        updateSmallerPartGeometryInput(
          areaId,
          partId,
          input
        );

        return true;
      }

      return false;
    }

    function renderMethodPanels(card, area) {
      const activeMethod =
        area.inputs.measurementComposition ===
          'smaller-parts'
          ? ''
          : area.inputs.measurementMethod || '';

      const methodInput = card.querySelector(
        '[data-ms-measurement-method]'
      );

      if (methodInput) {
        setInputValue(
          methodInput,
          activeMethod
        );
      }

      card
        .querySelectorAll(
          '[data-ms-method-panel]'
        )
        .forEach(function (panel) {
          panel.hidden =
            panel.getAttribute(
              'data-ms-method-panel'
            ) !== activeMethod;
        });

      card
        .querySelectorAll(
          '[data-ms-geometry-input]'
        )
        .forEach(function (input) {
          setInputValue(
            input,
            getGeometryInputValue(
              area,
              input
            )
          );
        });
    }

    function rectangleDimensions(area) {
      if (area.inputs.measurementMethod !== 'rectangle' || area.inputs.measurementComposition === 'smaller-parts') return null;
      const inputs = getMethodInputs(area);
      const length = geometry.normalizeLinearDimension(inputs.length);
      const width = geometry.normalizeLinearDimension(inputs.width);
      return length.status === rules.STATUS.VALID && width.status === rules.STATUS.VALID
        ? {length: length.feet, width: width.feet} : null;
    }

    function additionalRequirement(area, bundle) {
      if (bundle.contribution.status !== rules.STATUS.VALID) return {fabric: 0, staples: 0, sticks: 0, purchasedFeet: 0, connectors: 0, corners: 0};
      return rules.deriveAdditionalMaterials(area.inputs, bundle.geometryResult, rectangleDimensions(area), config.ancillaryRules);
    }

    function planDecisionState(area) {
      const related = area.inputs.relatedMaterials || {};
      const fabric = related.fabric || {}, staples = related.staples || {}, edge = related.alphaEdge || {};
      const yesNo = value => value === true ? 'yes' : value === false ? 'no' : null;
      return {
        purchaseMethod: rules.resolvePurchaseMethod(getSelectableMaterial(area.inputs.materialId), area.inputs.purchaseMethod),
        fabric: yesNo(fabric.include), overlap: String(fabric.allowancePercent || 15),
        staples: yesNo(staples.include), spacing: staples.spacingChoice || '8',
        customSpacingFeet: staples.customSpacingFeet,
        edging: yesNo(edge.include), extent: edge.extent,
        lengthSides: edge.lengthSides, widthSides: edge.widthSides, squareSides: edge.squareSides,
        manualFeet: edge.manualFeet, corners: yesNo(edge.cornersInclude), cornerQuantity: edge.ninetyDegreeConnectorsPerArea
      };
    }

    function renderPlanDecisions(card, area, bundle) {
      const values = planDecisionState(area);
      const material = getSelectableMaterial(area.inputs.materialId);
      const stages = areaWorkflowStages(card, area, bundle);
      const dims = rectangleDimensions(area);
      const square = dims && Math.abs(dims.length - dims.width) < 1e-9;
      const additional = additionalRequirement(area, bundle);
      const fabricDone = values.fabric === 'no' || (values.fabric === 'yes' && values.staples !== null &&
        (values.staples === 'no' || additional.staples > 0));
      const visibility = {
        purchaseMethod: stages.material && Boolean(material) && rules.purchaseMethods(material).length > 1,
        fabric: stages.result,
        overlap: stages.result && values.fabric === 'yes',
        staples: stages.result && values.fabric === 'yes',
        spacing: stages.result && values.fabric === 'yes' && values.staples === 'yes',
        edging: stages.result && fabricDone,
        extent: stages.result && fabricDone && values.edging === 'yes' && Boolean(rules.knownEdgingPerimeter(bundle.geometryResult.perimeterFacts)),
        squareSides: stages.result && fabricDone && values.edging === 'yes' && values.extent === 'partial' && square,
        lengthSides: stages.result && fabricDone && values.edging === 'yes' && values.extent === 'partial' && dims && !square,
        widthSides: stages.result && fabricDone && values.edging === 'yes' && values.extent === 'partial' && dims && !square,
        manualFeet: stages.result && fabricDone && values.edging === 'yes' &&
          (!rules.knownEdgingPerimeter(bundle.geometryResult.perimeterFacts) || (values.extent === 'partial' && !dims)),
        corners: stages.result && fabricDone && additional.sticks > 0 && Boolean(dims),
        cornerQuantity: stages.result && fabricDone && additional.sticks > 0 && Boolean(dims) && values.corners === 'yes'
      };
      card.querySelectorAll('[data-ms-plan-decision]').forEach(function (group) {
        const key = group.getAttribute('data-ms-plan-decision');
        group.hidden = !visibility[key];
        const value = values[key];
        let answered = value !== null && value !== undefined && value !== '';
        if (key === 'overlap') answered = Boolean(area.inputs.planConfirmed?.overlap || values.staples !== null);
        if (key === 'spacing') answered = (values.spacing !== 'custom' || additional.staples > 0) &&
          Boolean(area.inputs.planConfirmed?.spacing || values.edging !== null);
        if (key === 'manualFeet') answered = rules.normalizePositiveWholeNumber(value).status === rules.STATUS.VALID;
        const editing = card.msPlanEditing === key;
        const active = group.querySelector('[data-ms-plan-active]');
        const summary = group.querySelector('[data-ms-plan-summary]');
        const help = group.querySelector('[data-ms-plan-help]');
        if (help) {
          const helpId = 'ms-project-planner-' + area.id + '-' + key + '-help';
          help.setAttribute('id', helpId);
          active.setAttribute('aria-describedby', helpId);
        }
        active.hidden = answered && !editing;
        summary.hidden = !answered || editing;
        const buttons = group.querySelectorAll('[data-ms-plan-answer]');
        let label = value;
        buttons.forEach(function (button) {
          const selected = String(value) === button.value;
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          if (selected) label = button.textContent;
        });
        const labels = {purchaseMethod: 'Purchase method', fabric: 'Weed barrier fabric', overlap: 'Fabric overlap',
          staples: 'Landscape staples', spacing: 'Staple spacing', edging: 'Landscape edging', extent: 'Entire area',
          squareSides: 'Sides needing edging', manualFeet: 'Edging length', corners: '90-degree corner connectors', cornerQuantity: 'Corner connectors'};
        if ((key === 'lengthSides' || key === 'widthSides') && dims) {
          labels[key] = formatMeasurement(key === 'lengthSides' ? dims.length : dims.width) + '-foot sides';
          group.querySelector('legend').textContent = labels[key] + ':';
          group.querySelector('[data-ms-plan-change]').setAttribute('aria-label', 'Change: ' + labels[key]);
        }
        if (key === 'extent') label = value === 'entire' ? 'Yes' : 'No — only part of the area';
        if (key === 'manualFeet') label = value + ' feet';
        if (key === 'spacing' && value === 'custom') label = values.customSpacingFeet + ' feet';
        group.querySelector('[data-ms-plan-summary-text]').textContent = (labels[key] || key) + ': ' + (label || '');
      });
      const partial = card.querySelector('[data-ms-rectangle-partial]');
      if (partial) partial.hidden = !visibility.lengthSides;
      const partialError = card.querySelector('[data-ms-partial-edging-error]');
      if (partialError) partialError.hidden = !visibility.lengthSides || values.lengthSides !== '0' || values.widthSides !== '0';
      const custom = card.querySelector('[data-ms-custom-spacing]');
      if (custom) custom.hidden = values.spacing !== 'custom';
      card.querySelectorAll('[data-ms-plan-input]').forEach(function (input) {
        const key = input.getAttribute('data-ms-plan-input');
        setInputValue(input, values[key]);
        const error = card.querySelector('[data-ms-plan-error="' + key + '"]');
        const errorId = 'ms-project-planner-' + area.id + '-' + key + '-error';
        error.setAttribute('id', errorId);
        input.setAttribute('aria-describedby', errorId);
      });
      const outputs = {
        fabric: additional.fabric ? 'Weed barrier fabric needed: ' + additional.fabric + ' linear feet' : '',
        staples: additional.staples ? 'Landscape staples needed: ' + additional.staples + ' staples' : '',
        edging: additional.sticks ? 'Landscape edging needed: ' + additional.sticks + (additional.sticks === 1 ? ' stick (' : ' sticks (') + additional.purchasedFeet + ' feet)' : '',
        connectors: additional.connectors ? 'Connectors included: ' + additional.connectors : '',
        corners: additional.corners ? '90-degree corner connectors: ' + additional.corners : ''
      };
      card.querySelectorAll('[data-ms-additional-result]').forEach(function (node) {
        const key = node.getAttribute('data-ms-additional-result');
        node.textContent = outputs[key]; node.hidden = !outputs[key];
      });
      const extra = card.querySelector('[data-ms-extra-connectors]');
      if (extra) extra.hidden = !additional.sticks;
    }

    function updatePlanDecision(areaId, key, value) {
      const area = getArea(areaId);
      if (key === 'purchaseMethod') {
        const material = getSelectableMaterial(area.inputs.materialId);
        if (rules.purchaseMethods(material).includes(value)) state = core.updateAreaInputs(state, areaId, {purchaseMethod: value});
        return;
      }
      const fields = {
        fabric: ['fabric', 'include'], overlap: ['fabric', 'allowancePercent'],
        staples: ['staples', 'include'], spacing: ['staples', 'spacingChoice'], customSpacingFeet: ['staples', 'customSpacingFeet'],
        edging: ['alphaEdge', 'include'], extent: ['alphaEdge', 'extent'], lengthSides: ['alphaEdge', 'lengthSides'],
        widthSides: ['alphaEdge', 'widthSides'], squareSides: ['alphaEdge', 'squareSides'], manualFeet: ['alphaEdge', 'manualFeet'],
        corners: ['alphaEdge', 'cornersInclude'], cornerQuantity: ['alphaEdge', 'ninetyDegreeConnectorsPerArea']
      };
      const field = fields[key];
      if (!field) return;
      const allowed = {fabric: ['yes','no'], overlap: ['10','15','20'], staples: ['yes','no'], spacing: ['4','8','custom'],
        edging: ['yes','no'], extent: ['entire','partial'], lengthSides: ['0','1','2'], widthSides: ['0','1','2'],
        squareSides: ['1','2','3'], corners: ['yes','no'], cornerQuantity: ['1','2','3','4']};
      if (allowed[key] && !allowed[key].includes(String(value))) return;
      const normalized = ['fabric','staples','edging','corners'].includes(key) ? value === 'yes' : value;
      state = core.updateAreaRelatedMaterials(state, areaId, {[field[0]]: {[field[1]]: normalized}});
      if (revision && revision.areaId === areaId) {
        if (key === 'fabric' && normalized === false) {
          state = core.updateAreaRelatedMaterials(state, areaId, {
            fabric: {allowancePercent: 15}, staples: {include: null, spacingChoice: '8', customSpacingFeet: null}
          });
        }
        if (key === 'staples' && normalized === false) {
          state = core.updateAreaRelatedMaterials(state, areaId, {staples: {spacingChoice: '8', customSpacingFeet: null}});
        }
        if (key === 'edging' && normalized === false) {
          state = core.updateAreaRelatedMaterials(state, areaId, {alphaEdge: {
            extent: null, manualFeet: null, lengthSides: null, widthSides: null,
            squareSides: null, cornersInclude: null, ninetyDegreeConnectorsPerArea: null
          }});
        }
        if (key === 'corners' && normalized === false) {
          state = core.updateAreaRelatedMaterials(state, areaId, {alphaEdge: {ninetyDegreeConnectorsPerArea: null}});
        }
      }
      if (key === 'edging' && normalized === true &&
          !rules.knownEdgingPerimeter(deriveAreaBundle(getArea(areaId)).geometryResult.perimeterFacts)) {
        state = core.updateAreaRelatedMaterials(state, areaId, {alphaEdge: {extent: null}});
      }
      state = core.updateAreaInputs(state, areaId, {planConfirmed: {...area.inputs.planConfirmed, [key]: true}});
    }

    function handlePlanClick(event) {
      const button = event.target.closest('[data-ms-plan-answer], [data-ms-plan-change], [data-ms-plan-confirm]');
      if (!button) return;
      const card = button.closest('[data-ms-area-card]');
      if (!card) return;
      const areaId = card.getAttribute('data-ms-area-id');
      const change = button.getAttribute('data-ms-plan-change');
      if (change) {
        card.msPlanEditing = change;
        renderPlanDecisions(card, getArea(areaId), deriveAreaBundle(getArea(areaId)));
        const group = button.closest('[data-ms-plan-decision]');
        const first = group.querySelector('[aria-pressed="true"]') || group.querySelector('[data-ms-plan-input]') || group.querySelector('[data-ms-plan-answer]');
        if (first) first.focus();
        return;
      }
      const key = button.getAttribute('data-ms-plan-answer') || button.getAttribute('data-ms-plan-confirm');
      if (!key) return;
      let value = button.value;
      if (button.hasAttribute('data-ms-plan-confirm')) {
        const input = card.querySelector('[data-ms-plan-input="' + key + '"]');
        value = input.value;
        const valid = key === 'manualFeet' ? rules.normalizePositiveWholeNumber(value).status === rules.STATUS.VALID : Number(value) > 0 && Number.isFinite(Number(value));
        const error = card.querySelector('[data-ms-plan-error="' + key + '"]');
        error.hidden = valid;
        input.setAttribute('aria-invalid', valid ? 'false' : 'true');
        if (!valid) { input.focus(); return; }
      }
      updatePlanDecision(areaId, key, value);
      card.msPlanEditing = key === 'spacing' && value === 'custom' ? 'spacing' : null;
      rerenderArea(areaId);
      const groupKey = key === 'customSpacingFeet' ? 'spacing' : key;
      const group = card.querySelector('[data-ms-plan-decision="' + groupKey + '"]');
      const target = card.msPlanEditing ? group.querySelector('[data-ms-plan-input]') : group.querySelector('[data-ms-plan-change]');
      const body = card.querySelector('[data-ms-area-body]');
      if (body && body.hidden) {
        const changeArea = card.querySelector('[data-ms-change-area]');
        if (changeArea) changeArea.focus();
      } else if (target) target.focus();
    }

    function renderAreaResult(
      card,
      area,
      bundle
    ) {
      const resultStatus = card.querySelector(
        '[data-ms-result-status]'
      );
      const resultValue = card.querySelector(
        '[data-ms-result-value]'
      );
      const resultNumber = card.querySelector(
        '[data-ms-result-number]'
      );

      if (
        !resultStatus ||
        !resultValue ||
        !resultNumber
      ) {
        return;
      }

      renderMeasurementFacts(
        card,
        bundle.derived.measurement
      );

      resultValue.hidden = true;
      resultNumber.textContent = '';

      if (
        bundle.geometryResult.status ===
        geometry.STATUS.INVALID
      ) {
        resultStatus.textContent =
          'Please check the measurements you entered.';
        return;
      }

      if (
        bundle.geometryResult.status ===
        geometry.STATUS.INCOMPLETE
      ) {
        resultStatus.textContent =
          area.inputs.measurementMethod
            ? 'Please enter all the measurements for this area.'
            : 'Tell us how you want to measure this area.';
        return;
      }

      if (
        bundle.derived.status ===
        core.MEASUREMENT_STATUS.INVALID
      ) {
        resultStatus.textContent =
          'Enter the number of inches deep you want your material.';
        return;
      }

      if (
        bundle.derived.status ===
        core.MEASUREMENT_STATUS.INCOMPLETE
      ) {
        resultStatus.textContent =
          'Tell us how many inches deep you want your material.';
        return;
      }

      const multiplier =
        core.normalizeIdenticalAreaMultiplier(
          area.inputs.identicalAreaMultiplier
        );

      if (
        multiplier.status ===
        core.MEASUREMENT_STATUS.INVALID
      ) {
        resultStatus.textContent =
          'Please enter how many areas are this same size.';
        return;
      }

      if (
        multiplier.status ===
        core.MEASUREMENT_STATUS.INCOMPLETE
      ) {
        resultStatus.textContent =
          'Please enter how many areas are this same size.';
        return;
      }

      if (!getSelectableMaterial(area.inputs.materialId) || !rules.resolvePurchaseMethod(getSelectableMaterial(area.inputs.materialId), area.inputs.purchaseMethod)) {
        resultStatus.textContent = getSelectableMaterial(area.inputs.materialId) ? 'Choose a purchase method.' : 'Select a material';
        return;
      }

      const displayCubicYards =
        bundle.contribution.status ===
        core.MEASUREMENT_STATUS.VALID
          ? bundle.contribution.cubicYards
          : bundle.derived.volume.cubicYards *
            multiplier.multiplier;

      const bags = bundle.contribution.purchaseMethod === 'bags';
      resultNumber.textContent = bags
        ? String(rules.deriveBagQuantity(displayCubicYards, getSelectableMaterial(area.inputs.materialId).bagConversion).bagQuantity)
        : core.formatCubicYards(displayCubicYards, 2);
      const unit = card.querySelector('[data-ms-result-unit]');
      if (unit) unit.textContent = bags ? 'bags' : 'cubic yards';

      resultValue.hidden = false;

      if (multiplier.multiplier > 1) {
        resultStatus.textContent =
          'Includes ' +
          multiplier.multiplier +
          ' areas of the same size.';
        return;
      }

      resultStatus.textContent =
        'Calculated from the current area measurements and new-material depth.';
    }

    function renderAreaDetails(card, area, bundle) {
      const host = card.querySelector('[data-ms-area-details]');
      if (!host) return;
      host.replaceChildren();
      host.hidden = !expandedAreaIds.has(area.id) || Boolean(revision && revision.areaId === area.id);
      if (bundle.contribution.status !== core.MEASUREMENT_STATUS.VALID) return;
      const line = function (label, value) {
        if (value === null || value === undefined || value === '') return;
        const row = document.createElement('p');
        row.textContent = label + ': ' + value;
        host.appendChild(row);
      };
      line('Area Name', area.name || 'Area ' + area.number);
      const measurement = area.inputs.measurementComposition === 'smaller-parts'
        ? null : getMethodInputs(area);
      const dimensions = function (values, prefix) {
        Object.keys(values || {}).forEach(function (key) {
          const value = values[key];
          if (value && typeof value === 'object') {
            const feet = value.feet || 0;
            const inches = value.inches || 0;
            if (feet || inches) line(prefix + key.replace(/([A-Z])/g, ' $1'), feet + ' ft ' + inches + ' in');
          } else if (key === 'squareFeet' && value) {
            line(prefix + 'Entered square footage', value + ' sq ft');
          }
        });
      };
      if (measurement) dimensions(measurement, '');
      (area.inputs.measurementParts || []).forEach(function (part, index) {
        line('Section ' + (index + 1), part.measurementMethod);
        dimensions(part.measurementInputsByMethod && part.measurementInputsByMethod[part.measurementMethod], 'Section ' + (index + 1) + ' ');
      });
      if (bundle.derived.measurement.status === core.MEASUREMENT_STATUS.VALID) {
        line('Calculated square footage', formatMeasurement(bundle.derived.measurement.squareFeet) + ' sq ft');
      }
      const count = core.normalizeIdenticalAreaMultiplier(area.inputs.identicalAreaMultiplier);
      if (count.status === core.MEASUREMENT_STATUS.VALID && count.multiplier > 1) {
        line('Areas of the same size', count.multiplier);
      }
      const material = getSelectableMaterial(area.inputs.materialId);
      line('Material', material && material.label);
      line('Depth', area.inputs.depthInches && area.inputs.depthInches + ' in');
      line('Purchase method', bundle.contribution.purchaseMethod);
      const related = area.inputs.relatedMaterials || {};
      const additional = additionalRequirement(area, bundle);
      const fabric = related.fabric || {};
      const staples = related.staples || {};
      const edge = related.alphaEdge || {};
      if (fabric.include === false) line('Fabric', 'No');
      if (fabric.include === true) {
        line('Fabric', 'Yes — ' + fabric.allowancePercent + '% allowance; ' + additional.fabric + ' linear feet, 3 ft wide');
        line('Staples', staples.include ? additional.staples + ' at ' + (staples.spacingChoice === 'custom' ? staples.customSpacingFeet : staples.spacingChoice) + ' ft spacing' : 'No');
      }
      if (edge.include === false) line('Landscape edging', 'No');
      if (edge.include === true) {
        line('Landscape edging', additional.sticks + ' sticks, ' + additional.purchasedFeet + ' linear feet' + (edge.manualFeet ? '; entered length ' + edge.manualFeet + ' ft' : edge.extent ? '; ' + edge.extent + ' area' : ''));
        if (edge.cornersInclude === false) line('90° corner connectors', 'No');
        if (additional.corners) line('90° corner connectors', additional.corners);
      }
      line('Area Result', purchaseQuantityText(bundle.contribution));
    }

    function renderAreaHeader(card, area, index, bundle) {
      const heading = card.querySelector('[data-ms-area-heading]');
      const summary = card.querySelector('[data-ms-area-summary]');
      const toggle = card.querySelector('[data-ms-toggle-area]');
      const deleteButton = card.querySelector('[data-ms-delete-area]');
      const body = card.querySelector('[data-ms-area-body]');
      const deleteConfirmation = card.querySelector(
        '[data-ms-delete-confirmation]'
      );

      const complete = bundle.contribution.status === core.MEASUREMENT_STATUS.VALID && revisionIsComplete(area);

      const collapsed =
        complete && !(revision && revision.areaId === area.id);

      if (heading) {
        heading.textContent =
          area.name.trim() !== ''
            ? 'Area ' + (area.number || index + 1) + ' — ' + area.name.trim()
            : 'Area ' + (area.number || index + 1);
      }

      if (summary) {
        if (collapsed) {
          const materialLabel =
            materialLabels.get(bundle.contribution.materialId) ||
            bundle.contribution.materialId;

          summary.textContent =
            materialLabel +
            ' — ' +
            purchaseQuantityText(bundle.contribution);
          summary.hidden = false;
        } else {
          summary.textContent = '';
          summary.hidden = true;
        }
      }

      if (toggle) {
        toggle.hidden = !complete;
        toggle.textContent = 'AREA DETAILS';
        toggle.setAttribute('aria-label', 'Area ' + (area.number || index + 1) + ' details');
        toggle.setAttribute(
          'aria-expanded',
          expandedAreaIds.has(area.id) ? 'true' : 'false'
        );
      }

      if (body) {
        body.hidden = collapsed;
      }

      if (deleteButton) {
        deleteButton.hidden = !complete || Boolean(revision);
      }

      const changeButton = card.querySelector('[data-ms-change-area]');
      if (changeButton) changeButton.hidden = !complete || Boolean(revision);
      const revisionActions = card.querySelector('[data-ms-revision-actions]');
      if (revisionActions) revisionActions.hidden = !revision || revision.areaId !== area.id;
      const saveButton = card.querySelector('[data-ms-save-area-changes]');
      if (saveButton) saveButton.disabled = !revision || revision.areaId !== area.id || !revisionIsComplete(area);
      const confirmationText = deleteConfirmation && typeof deleteConfirmation.querySelector === 'function' && deleteConfirmation.querySelector('p');
      if (confirmationText) confirmationText.textContent = 'Remove Area ' + (area.number || index + 1) + (area.name.trim() ? ' — ' + area.name.trim() : '') + '? This will remove this Area from your project and update your project totals.';

      if (deleteConfirmation) {
        deleteConfirmation.hidden =
          pendingDeleteAreaId !== area.id;
      }
      renderAreaDetails(card, area, bundle);
    }

    function areaWorkflowStages(card, area, bundle) {
      const measurement = !area.inputs.measurementGuidanceStep &&
        (!card.msGuidanceEditing || card.msGuidanceEditing === 'same-size') &&
        (area.inputs.measurementComposition !== 'smaller-parts' || smallerPartsWorkflowIsComplete(area)) &&
        bundle.geometryResult.status === core.MEASUREMENT_STATUS.VALID;
      const answer = area.inputs.sameSizeAreas;
      const material = measurement && !card.msGuidanceEditing &&
        (answer === 'no' || (answer === 'yes' &&
          core.normalizeIdenticalAreaMultiplier(area.inputs.identicalAreaMultiplier).status === core.MEASUREMENT_STATUS.VALID));
      const depth = material && Boolean(rules.resolvePurchaseMethod(getSelectableMaterial(area.inputs.materialId), area.inputs.purchaseMethod));
      const result = depth && bundle.contribution.status === core.MEASUREMENT_STATUS.VALID;
      return {measurement, material, depth, result};
    }

    function revisionIsComplete(area) {
      const bundle = deriveAreaBundle(area);
      if (bundle.contribution.status !== core.MEASUREMENT_STATUS.VALID) return false;
      const related = area.inputs.relatedMaterials || {};
      const fabric = related.fabric || {};
      const staples = related.staples || {};
      const edge = related.alphaEdge || {};
      if (typeof fabric.include !== 'boolean' || typeof edge.include !== 'boolean') return false;
      if (fabric.include && typeof staples.include !== 'boolean') return false;
      if (fabric.include && staples.include &&
          rules.deriveSpacedStaples(1, staples.spacingChoice === 'custom' ? staples.customSpacingFeet : staples.spacingChoice).status !== rules.STATUS.VALID) return false;
      if (edge.include) {
        const known = rules.knownEdgingPerimeter(bundle.geometryResult.perimeterFacts);
        const dims = rectangleDimensions(area);
        if (known && !['entire', 'partial'].includes(edge.extent)) return false;
        if (!known && !edge.manualFeet && !dims) return false;
        if (additionalRequirement(area, bundle).sticks <= 0) return false;
        if (dims && typeof edge.cornersInclude !== 'boolean') return false;
        if (dims && edge.cornersInclude && ![1, 2, 3, 4].includes(Number(edge.ninetyDegreeConnectorsPerArea))) return false;
      }
      return true;
    }

    function renderAreaWorkflow(card, area, bundle) {
      const error = card.querySelector('[data-ms-triangle-area-error]');
      if (error) error.hidden = area.inputs.measurementMethod !== 'triangle' ||
        Boolean(area.inputs.measurementComposition) || bundle.geometryResult.status !== rules.STATUS.INVALID;
      const availability = card.querySelector('[data-ms-material-availability]');
      if (availability) {
        const material = getSelectableMaterial(area.inputs.materialId);
        availability.hidden = !material || material.availability !== 'temporarily-unavailable';
        availability.textContent = availability.hidden ? '' : 'This material is temporarily unavailable.';
      }
      const stages = areaWorkflowStages(card, area, bundle);
      const visibility = {
        '[data-ms-material-stage]': stages.material,
        '[data-ms-depth-group]': stages.depth,
        '[data-ms-count-stage]': stages.measurement && !card.msGuidanceEditing && area.inputs.sameSizeAreas === 'yes',
        '[data-ms-area-result-stage]': stages.result,
        '[data-ms-related-stage]': stages.result
      };
      Object.keys(visibility).forEach(function (selector) {
        const element = card.querySelector(selector);
        if (element) element.hidden = !visibility[selector];
      });
    }

    function renderAreaCard(card, area, index, preservePartControls) {
      const bundle = deriveAreaBundle(area);
      const smallerPartsActive =
        area.inputs.measurementComposition ===
          'smaller-parts';
      const smallerPartsIncomplete =
        smallerPartsActive &&
        !smallerPartsWorkflowIsComplete(area);


      card.setAttribute(
        'data-ms-existing-material-state',
        bundle.materialState.status
      );

      if (
        bundle.materialState.status ===
          'blocked-not-offered' &&
        bundle.materialState.label
      ) {
        card.setAttribute(
          'data-ms-existing-material-label',
          bundle.materialState.label
        );
      } else if (
        typeof card.removeAttribute === 'function'
      ) {
        card.removeAttribute(
          'data-ms-existing-material-label'
        );
      }

      const areaName = card.querySelector(
        '[data-ms-area-name]'
      );
      const methodInput = card.querySelector(
        '[data-ms-measurement-method]'
      );
      const depthInput = card.querySelector(
        '[data-ms-depth-inches]'
      );
      const materialInput = card.querySelector(
        '[data-ms-material]'
      );
      const multiplierInput = card.querySelector(
        '[data-ms-area-multiplier]'
      );

      if (areaName) {
        setInputValue(areaName, area.name);
      }

      if (methodInput) {
        setInputValue(
          methodInput,
          area.inputs.measurementMethod || ''
        );
      }

      if (depthInput) {
        setInputValue(
          depthInput,
          area.inputs.depthInches
        );
      }

      if (materialInput) {
        setInputValue(
          materialInput,
          area.inputs.materialId || ''
        );
      }

      [
        materialInput,
        depthInput,
        multiplierInput
      ].forEach(function (input) {
        if (!input) {
          return;
        }

        const field = input.closest(
          '.ms-project-planner__field'
        );

        if (field) {
          field.hidden = smallerPartsIncomplete || Boolean(area.inputs.measurementGuidanceStep);
        }
      });

      renderDepthChoices(card, area);

      if (multiplierInput) {
        setInputValue(
          multiplierInput,
          area.inputs.identicalAreaMultiplier
        );
      }

      if (!preservePartControls) renderMeasurementGuidance(card, area, bundle);
      renderSmallerParts(card, area, preservePartControls);
      renderMethodPanels(card, area);
      if (!smallerPartsIncomplete) {
        renderPlanDecisions(card, area, bundle);
      } else {
        const fabricOptions = card.querySelector(
          '[data-ms-fabric-options]'
        );
        if (fabricOptions) {
          fabricOptions.hidden = true;
        }
      }
      if (!smallerPartsIncomplete) {
        renderAreaResult(card, area, bundle);
      } else {
        renderIntermediateSmallerPartsFacts(card, area);
        const resultStatus = card.querySelector(
          '[data-ms-result-status]'
        );
        const resultValue = card.querySelector(
          '[data-ms-result-value]'
        );
        if (resultStatus) {
          resultStatus.textContent = '';
        }
        if (resultValue) {
          resultValue.hidden = true;
        }
      }
      renderAreaWorkflow(card, area, bundle);
      renderAreaHeader(
        card,
        area,
        index,
        bundle
      );
    }

    function renderAreaList() {
      areaList.replaceChildren();

      state.areas.forEach(function (area, index) {
        const card = createAreaCard(area);

        renderAreaCard(
          card,
          area,
          index
        );

        areaList.appendChild(card);
      });
    }

    function deriveProjectMaterialWeight(
      total
    ) {
      const material = config.materials.find(
        function (candidate) {
          return candidate.id === total.materialId;
        }
      );

      if (!material) {
        return {
          status: rules.STATUS.UNAVAILABLE
        };
      }

      return rules.deriveEstimatedWeight(
        total.cubicYards,
        material.weight
      );
    }

    function formatProjectMaterialWeight(
      weight
    ) {
      if (
        !weight ||
        weight.status !== rules.STATUS.VALID
      ) {
        return null;
      }

      if (weight.type === 'single') {
        const pounds =
          rules.roundPoundsForDisplay(
            weight.pounds
          );
        const tons =
          rules.formatTonsForDisplay(
            weight.pounds
          );

        if (pounds === null) {
          return null;
        }

        let text =
          'Estimated weight: ' +
          pounds.toLocaleString('en-US') +
          ' lb';

        if (tons !== null) {
          text +=
            ' (' +
            tons +
            ' U.S. tons)';
        }

        return text;
      }

      if (weight.type === 'range') {
        const minimumPounds =
          rules.roundPoundsForDisplay(
            weight.minimumPounds
          );
        const maximumPounds =
          rules.roundPoundsForDisplay(
            weight.maximumPounds
          );
        const minimumTons =
          rules.formatTonsForDisplay(
            weight.minimumPounds
          );
        const maximumTons =
          rules.formatTonsForDisplay(
            weight.maximumPounds
          );

        if (
          minimumPounds === null ||
          maximumPounds === null
        ) {
          return null;
        }

        const poundsText =
          minimumPounds.toLocaleString('en-US') +
          '–' +
          maximumPounds.toLocaleString('en-US') +
          ' lb';

        if (
          minimumTons !== null &&
          maximumTons !== null
        ) {
          return (
            'Estimated weight: ' +
            poundsText +
            ' (' +
            minimumTons +
            '–' +
            maximumTons +
            ' U.S. tons)'
          );
        }

        if (maximumTons !== null) {
          return (
            'Estimated weight: ' +
            poundsText +
            ' (maximum ' +
            maximumTons +
            ' U.S. tons)'
          );
        }

        return (
          'Estimated weight: ' +
          poundsText
        );
      }

      return null;
    }

    function renderStartOverVisibility() {
      if (startOverButton) {
        startOverButton.hidden =
          state.project.name === '' &&
          state.areas.length === 1 &&
          state.areas[0].name === '' &&
          JSON.stringify(state.areas[0].inputs) === blankAreaInputs &&
          !areaList.querySelector('[data-ms-guidance-step] [aria-pressed="true"]');
      }
    }

    function purchaseQuantityText(total) {
      const material = getSelectableMaterial(total.materialId);
      return total.purchaseMethod === 'bags'
        ? rules.deriveBagQuantity(total.cubicYards, material.bagConversion).bagQuantity + ' bags'
        : Number(core.formatCubicYards(total.cubicYards, 2)) + ' cubic yards';
    }

    function appendProductLink(container, product) {
      if (!product || !product.productInfoUrl) return;
      const link = document.createElement('a');
      link.textContent = 'View Product →';
      link.setAttribute('href', product.productInfoUrl);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.setAttribute('class', 'ms-project-planner__product-link');
      container.appendChild(link);
    }

    function appendSummaryLine(container, text) {
      const line = document.createElement('p');
      line.textContent = text;
      container.appendChild(line);
    }

    const BULK_EXPLANATION = 'Cubic-yard quantities shown are calculated as full cubic yards. If purchasing bulk material elsewhere, confirm that the quantity being sold represents the full cubic-yard quantity shown on your material list. Mulch & Stuff loads bulk materials using a 1.0-cubic-yard CAT bucket.';
    const COMPARE_TEXT = 'See how the amount of material being sold can affect the real cost of your purchase.';
    const COMPARE_URL = 'https://mulchandstuff.com/#price-quantity-comparison-calculator';
    const compareLink = root.querySelector('[data-ms-compare-price] a');
    const previewThemeId = typeof window !== 'undefined' && window.location
      ? new URLSearchParams(window.location.search).get('preview_theme_id') : null;
    if (compareLink && /^\d+$/.test(previewThemeId || '')) {
      compareLink.href = 'https://mulchandstuff.com/?preview_theme_id=' +
        previewThemeId + '#price-quantity-comparison-calculator';
    }
    const WEIGHT_NOTE = 'Estimated weight only. Actual material weight may vary due to moisture, material composition, particle size, and other conditions.';

    function buildProjectMaterialList() {
      const areas = typeof revision !== 'undefined' && revision ? state.areas.map(area => area.id === revision.areaId ? revision.committedArea : area) : state.areas;
      const bundles = areas.map(area => deriveAreaBundle(area));
      const aggregate = core.aggregateMaterialTotals(bundles.map(bundle => bundle.contribution));
      const primary = aggregate.totals.map(function (total) {
        const material = getSelectableMaterial(total.materialId);
        return {
          name: material.label,
          method: total.purchaseMethod === 'bags' ? 'Bags' : 'Bulk',
          quantity: purchaseQuantityText(total),
          weight: formatProjectMaterialWeight(deriveProjectMaterialWeight(total)),
          productUrl: material.productInfoUrl || null
        };
      });
      const totals = {fabric: 0, staples: 0, sticks: 0, purchasedFeet: 0, connectors: 0, corners: 0};
      areas.forEach(function (area, index) {
        if (bundles[index].contribution.status !== core.MEASUREMENT_STATUS.VALID) return;
        const additional = additionalRequirement(area, bundles[index]);
        Object.keys(totals).forEach(key => { totals[key] += additional[key]; });
      });
      const additional = [];
      if (totals.fabric) additional.push({name: 'Weed barrier fabric', quantity: totals.fabric + ' linear feet (3-foot width)', productUrl: config.ancillaryRules.fabric.productInfoUrl});
      if (totals.staples) additional.push({name: 'Landscape staples', quantity: totals.staples + ' staples', productUrl: config.ancillaryRules.staples.productInfoUrl});
      if (totals.sticks) additional.push({name: 'Landscape edging', quantity: totals.sticks + (totals.sticks === 1 ? ' stick' : ' sticks') + ' (' + totals.purchasedFeet + ' linear feet)', subordinate: 'Connectors included: ' + totals.connectors, productUrl: config.ancillaryRules.alphaEdge.productInfoUrl});
      if (totals.corners) additional.push({name: '90° corner connectors', quantity: String(totals.corners), productUrl: config.ancillaryRules.corners.productInfoUrl});
      return {
        projectName: state.project.name.trim(), primary, additional,
        bagGuidance: primary.some(item => item.method === 'Bags') ? 'Individual bags typically weigh approximately 40–50 lb.' : null,
        weightNote: primary.some(item => item.weight) ? WEIGHT_NOTE : null,
        bulkExplanation: primary.some(item => item.method === 'Bulk') ? BULK_EXPLANATION : null,
        compareText: primary.some(item => item.method === 'Bulk') ? COMPARE_TEXT : null,
        compareUrl: COMPARE_URL
      };
    }

    function materialListText(model) {
      const lines = ['PROJECT MATERIAL LIST'];
      if (model.projectName) lines.push('', 'Project: ' + model.projectName);
      model.primary.forEach(function (entry) {
        lines.push('', entry.name + ' — ' + entry.method + ': ' + entry.quantity);
        if (entry.weight) lines.push(entry.weight);
      });
      if (model.bagGuidance) lines.push('', model.bagGuidance);
      if (model.weightNote) lines.push('', 'Estimated Weight Note', model.weightNote);
      if (model.additional.length) {
        lines.push('', 'Additional Materials');
        model.additional.forEach(function (entry) {
          lines.push(entry.name + ': ' + entry.quantity);
          if (entry.subordinate) lines.push(entry.subordinate);
        });
      }
      if (model.bulkExplanation) lines.push('', 'ABOUT YOUR BULK QUANTITY', model.bulkExplanation);
      if (model.compareText) lines.push('', 'COMPARE PRICE & QUANTITY', model.compareText, model.compareUrl);
      lines.push('', 'Mulch & Stuff', '760 S. Nova Road', 'Ormond Beach, FL 32174', '386-302-6444', 'mulchandstuff.com');
      return lines.join('\n');
    }

    function renderPrintList(model) {
      const host = root.querySelector('[data-ms-print-dynamic]');
      if (!host) return;
      host.replaceChildren();
      const add = function (tag, content, parent) {
        const node = document.createElement(tag);
        node.textContent = content;
        (parent || host).appendChild(node);
        return node;
      };
      if (model.projectName) add('p', 'Project: ' + model.projectName);
      add('p', 'Prepared: ' + new Date().toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'}));
      model.primary.forEach(function (entry) {
        const item = add('div', '');
        const name = entry.productUrl ? add('a', entry.name, item) : add('span', entry.name, item);
        if (entry.productUrl) name.setAttribute('href', entry.productUrl);
        add('span', ' — ' + entry.method + ': ' + entry.quantity, item);
        if (entry.weight) add('p', entry.weight, item);
      });
      if (model.bagGuidance) add('p', model.bagGuidance);
      if (model.weightNote) { add('h2', 'Estimated Weight Note'); add('p', model.weightNote); }
      if (model.additional.length) {
        add('h2', 'Additional Materials');
        model.additional.forEach(function (entry) {
          const item = add('div', '');
          const name = entry.productUrl ? add('a', entry.name, item) : add('span', entry.name, item);
          if (entry.productUrl) name.setAttribute('href', entry.productUrl);
          add('span', ': ' + entry.quantity, item);
          if (entry.subordinate) add('p', entry.subordinate, item);
        });
      }
      if (model.bulkExplanation) { add('h2', 'ABOUT YOUR BULK QUANTITY'); add('p', model.bulkExplanation); }
      if (model.compareText) {
        add('h2', 'COMPARE PRICE & QUANTITY');
        add('p', model.compareText);
        add('a', 'COMPARE PRICE & QUANTITY', host).setAttribute('href', model.compareUrl);
      }
    }

    function renderSummary() {
      renderStartOverVisibility();
      const model = buildProjectMaterialList();
      summaryResults.replaceChildren();
      summaryResults.hidden = !model.primary.length;
      summaryStatus.textContent = model.primary.length ? '' : 'Complete an area to begin the project material list.';
      summaryStatus.hidden = Boolean(model.primary.length);
      model.primary.forEach(function (entry) {
        const item = document.createElement('div');
        item.setAttribute('class', 'ms-project-planner__summary-item');
        appendSummaryLine(item, entry.name + ' — ' + entry.method + ': ' + entry.quantity);
        if (entry.weight) appendSummaryLine(item, entry.weight);
        appendProductLink(item, {productInfoUrl: entry.productUrl});
        summaryResults.appendChild(item);
      });
      const bagGuidance = root.querySelector('[data-ms-bag-guidance]');
      if (bagGuidance) { bagGuidance.hidden = !model.bagGuidance; bagGuidance.textContent = model.bagGuidance || ''; }
      if (summaryWeightDisclaimer) summaryWeightDisclaimer.hidden = !model.weightNote;
      const section = root.querySelector('[data-ms-related-materials-summary]');
      const results = root.querySelector('[data-ms-additional-summary-results]');
      if (section && results) {
        results.replaceChildren();
        section.hidden = !model.additional.length;
        model.additional.forEach(function (entry) {
          const item = document.createElement('div');
          item.setAttribute('class', 'ms-project-planner__summary-item');
          appendSummaryLine(item, entry.name + ': ' + entry.quantity);
          if (entry.subordinate) appendSummaryLine(item, entry.subordinate);
          appendProductLink(item, {productInfoUrl: entry.productUrl});
          results.appendChild(item);
        });
      }
      const bulk = root.querySelector('[data-ms-bulk-explanation]');
      if (bulk) bulk.hidden = !model.bulkExplanation;
      const compare = root.querySelector('[data-ms-compare-price]');
      if (compare) compare.hidden = !model.compareText;
      const controls = root.querySelector('[data-ms-list-controls]');
      if (controls) controls.hidden = !model.primary.length;
      const manual = root.querySelector('[data-ms-manual-copy]');
      if (manual) manual.hidden = true;
      const copyStatus = root.querySelector('[data-ms-copy-status]');
      if (copyStatus) copyStatus.textContent = '';
      renderPrintList(model);
    }

    function updateAreaName(areaId, value) {
      state = core.updateAreaName(
        state,
        areaId,
        value
      );
    }

    function setMeasurementGuidanceStep(
      card,
      step,
      visible
    ) {
      const element = card.querySelector(
        '[data-ms-guidance-step="' +
          step +
          '"]'
      );

      if (element) {
        element.hidden = !visible;
      }
    }

    function setMeasurementGuidanceChoice(
      card,
      selector,
      value
    ) {
      card
        .querySelectorAll(selector)
        .forEach(function (input) {
          input.setAttribute('aria-pressed', String(input.value === value));
        });
    }

    function renderMeasurementGuidance(card, area, bundle) {
      const steps = new Map();
      const choices = new Map();
      collectMeasurementGuidance(card, area,
        function (_, step, visible) { steps.set(step, visible); },
        function (_, selector, value) { choices.set(selector, value); }
      );
      const committedChoice = area.inputs.squareFootageChoice !== undefined
        ? area.inputs.squareFootageChoice
        : area.inputs.measurementMethod === 'known-square-footage' ? 'known'
          : area.inputs.measurementMethod || area.inputs.measurementGuidanceStep ||
            area.inputs.measurementComposition ? 'unknown' : null;
      steps.set('square-footage', true);
      card.querySelectorAll('[data-ms-guidance-square-footage]').forEach(function (button) {
        button.hidden = false;
        button.setAttribute('aria-pressed', String(button.getAttribute('data-ms-guidance-square-footage') === committedChoice));
      });
      const answers = area.inputs.guidanceAnswers || card.msGuidanceAnswers || {};
      card.msGuidanceAnswers = answers;
      Object.keys(answers).forEach(step => steps.set(step, true));
      steps.set('same-size', areaWorkflowStages(card, area, bundle || deriveAreaBundle(area)).measurement);
      choices.set('[data-ms-guidance-same-size]', area.inputs.sameSizeAreas || null);
      // The collected dependency order also bounds reconsideration. Keep committed
      // state intact; only expose the path through the question being edited.
      let downstream = false;
      steps.forEach(function (visible, step) {
        if (downstream) steps.set(step, false);
        if (step === card.msGuidanceEditing) downstream = true;
      });
      card.querySelectorAll('[data-ms-guidance-dependent]').forEach(function (element) {
        element.hidden = Boolean(card.msGuidanceEditing);
      });
      // Never transiently hide a surviving focused question or reset its selected answer.
      steps.forEach(function (visible, step) {
        setMeasurementGuidanceStep(card, step, visible);
      });
      choices.forEach(function (value, selector) {
        const step = selector.slice('[data-ms-guidance-'.length, -1);
        const answers = card.msGuidanceAnswers || {};
        setMeasurementGuidanceChoice(card, selector,
          steps.get(step) && Object.prototype.hasOwnProperty.call(answers, step)
            ? answers[step] : value);
      });
      steps.forEach(function (visible, step) {
        const group = card.querySelector('[data-ms-guidance-step="' + step + '"]');
        if (!group || !group.querySelector) return;
        const active = group.querySelector('[data-ms-guidance-active]');
        const summary = group.querySelector('[data-ms-guidance-summary]');
        if (!active || !summary) return;
        const value = step === 'square-footage' ? committedChoice
          : answers[step] || choices.get('[data-ms-guidance-' + step + ']');
        const completed = Boolean(value) && card.msGuidanceEditing !== step;
        const moveFocus = completed && active.contains(document.activeElement);
        active.hidden = completed;
        summary.hidden = !completed;
        if (moveFocus) summary.querySelector('[data-ms-guidance-change]').focus();
        const answer = step === 'square-footage'
          ? value === 'known' ? 'Known' : 'Help me figure it out'
          : value === 'known' ? 'Enter manually'
            : value === 'yes' ? 'Yes' : value === 'unsure' ? "I’m not sure" : 'No';
        summary.querySelector('[data-ms-guidance-summary-text]').textContent =
          active.getAttribute('data-ms-guidance-label') +
          (active.getAttribute('data-ms-guidance-label').endsWith('?') ? ' ' : ': ') + answer;
      });
    }

    function collectMeasurementGuidance(card, area, setStep, setChoice) {
      const method =
        area.inputs.measurementMethod || '';

      [
        'square-footage',
        'box',
        'around',
        'around-round',
        'around-recovery',
        'round-area',
        'triangle',
        'smaller-parts'
      ].forEach(function (step) {
        setStep(
          card,
          step,
          false
        );
      });

      [
        '[data-ms-guidance-box]',
        '[data-ms-guidance-around]',
        '[data-ms-guidance-around-round]',
        '[data-ms-guidance-round-area]',
        '[data-ms-guidance-triangle]'
      ].forEach(function (selector) {
        setChoice(
          card,
          selector,
          null
        );
      });

      if (
        area.inputs.measurementComposition ===
          'smaller-parts'
      ) {
        setStep(
          card,
          'smaller-parts',
          true
        );
        return;
      }

      const pendingStep = area.inputs.measurementGuidanceStep === 'around-recovery'
        ? (area.inputs.guidanceAnswers || {})['around-round'] === 'unsure'
          ? 'smaller-parts' : 'round-area'
        : area.inputs.measurementGuidanceStep;
      if (pendingStep) {
        const aroundBorder = pendingStep === 'around-round' ||
          (pendingStep === 'smaller-parts' && ['no', 'unsure'].includes((area.inputs.guidanceAnswers || {})['around-round']));
        const steps = ['square-footage', 'box'];
        if (pendingStep !== 'box') steps.push('around');
        if (aroundBorder && (area.inputs.guidanceAnswers || {}).around !== 'unsure') steps.push('around-round');
        if (!aroundBorder && ['round-area', 'triangle', 'smaller-parts'].includes(pendingStep)) steps.push('round-area');
        if (!aroundBorder && ['triangle', 'smaller-parts'].includes(pendingStep)) steps.push('triangle');
        if (pendingStep === 'smaller-parts') steps.push('smaller-parts');
        steps.forEach(step => setStep(card, step, true));
        if (pendingStep !== 'box') setChoice(card, '[data-ms-guidance-box]', 'no');
        if (aroundBorder || steps.includes('round-area')) {
          setChoice(card, '[data-ms-guidance-around]', aroundBorder ? 'yes' : 'no');
        }
        if (steps.includes('triangle')) setChoice(card, '[data-ms-guidance-round-area]', 'no');
        if (pendingStep === 'smaller-parts' && !aroundBorder) setChoice(card, '[data-ms-guidance-triangle]', 'no');
        return;
      }

      setStep(card, 'square-footage', true);

      if (!method || method === 'known-square-footage') {
        return;
      }

      setStep(card, 'box', true);

      if (method === 'rectangle') {
        setChoice(
          card,
          '[data-ms-guidance-box]',
          'yes'
        );
        return;
      }

      setChoice(
        card,
        '[data-ms-guidance-box]',
        'no'
      );

      setStep(
        card,
        'around',
        true
      );

      if (method === 'round-border') {
        setChoice(
          card,
          '[data-ms-guidance-around]',
          'yes'
        );

        setStep(
          card,
          'around-round',
          true
        );

        setChoice(
          card,
          '[data-ms-guidance-around-round]',
          'yes'
        );

        return;
      }

      setChoice(
        card,
        '[data-ms-guidance-around]',
        'no'
      );

      setStep(
        card,
        'round-area',
        true
      );

      if (method === 'round-area') {
        setChoice(
          card,
          '[data-ms-guidance-round-area]',
          'yes'
        );

        return;
      }

      setChoice(
        card,
        '[data-ms-guidance-round-area]',
        'no'
      );

      setStep(
        card,
        'triangle',
        true
      );

      if (method === 'triangle') {
        setChoice(
          card,
          '[data-ms-guidance-triangle]',
          'yes'
        );
      }
    }

    function resolveGuidedMeasurementMethod(
      card,
      areaId,
      method
    ) {
      updateMeasurementMethod(
        areaId,
        method
      );

      const area = getArea(areaId);

      if (!area) {
        rerender();
        return;
      }

      rerenderArea(areaId);
    }

    function setPendingMeasurementGuidance(areaId, step, deferRender) {
      state = core.updateAreaInputs(state, areaId, {
        measurementMethod: null,
        measurementComposition: null,
        measurementPartsDone: false,
        measurementGuidanceStep: step,
        measurementInputsByMethod: Object.fromEntries(
          Object.keys(getArea(areaId).inputs.measurementInputsByMethod || {})
            .map(method => [method, method === 'triangle'
              ? getArea(areaId).inputs.measurementInputsByMethod.triangle : {}])
        ),
        measurementParts: []
      });
      if (!deferRender) rerenderArea(areaId);
    }

    function handleMeasurementGuidanceInput(card, areaId, input) {
      const transitions = [
        ['box', 'rectangle', 'around'],
        ['around', null, 'round-area'],
        ['around-round', 'round-border', 'smaller-parts'],
        ['round-area', 'round-area', 'triangle'],
        ['triangle', 'triangle', 'smaller-parts']
      ];
      for (const [step, method, next] of transitions) {
        if (!input.matches('[data-ms-guidance-' + step + ']')) continue;
        // Retain the exact committed answer (including unsure), clearing dependents.
        const answers = {...(getArea(areaId).inputs.guidanceAnswers || card.msGuidanceAnswers || {})};
        card.msGuidanceEditing = null;
        if (answers[step] === input.value) {
          rerenderArea(areaId);
          return true;
        }
        delete answers['around-recovery'];
        transitions.slice(transitions.findIndex(entry => entry[0] === step))
          .forEach(entry => { delete answers[entry[0]]; });
        answers[step] = input.value;
        card.msGuidanceAnswers = answers;
        state = core.updateAreaInputs(state, areaId, {guidanceAnswers: answers});
        setPendingMeasurementGuidance(areaId, null, true);
        if (input.value === 'yes' && method) {
          resolveGuidedMeasurementMethod(card, areaId, method);
        } else {
          setPendingMeasurementGuidance(areaId,
            step === 'around' && input.value === 'yes' ? 'around-round'
              : step === 'around-round' && input.value !== 'yes' ? 'smaller-parts' : next);
        }
        return true;
      }
      return false;
    }

    function updateMeasurementMethod(
      areaId,
      value
    ) {
      state = core.updateAreaInputs(
        state,
        areaId,
        {
          measurementMethod:
            value || null,
          measurementComposition: null,
          measurementGuidanceStep: null,
          measurementPartsDone: false
        }
      );
      if (value === 'known-square-footage' || (revision && revision.areaId === areaId && value !== 'rectangle')) {
        const related = getArea(areaId).inputs.relatedMaterials || {};
        state = core.updateAreaInputs(state, areaId, {relatedMaterials: {
          ...related, alphaEdge: {...(related.alphaEdge || {}),
            ...(value === 'known-square-footage' ? {extent: null} : {}),
            cornersInclude: null, ninetyDegreeConnectorsPerArea: null,
            lengthSides: null, widthSides: null, squareSides: null}
        }});
      }
    }

    function updateGeometryInput(
      areaId,
      input
    ) {
      const method =
        input.getAttribute(
          'data-ms-method'
        );
      const field =
        input.getAttribute(
          'data-ms-field'
        );
      const unit =
        input.getAttribute(
          'data-ms-unit'
        );
      const area = getArea(areaId);

      if (!area || !method || !field) {
        return;
      }

      const existing =
        area.inputs
          .measurementInputsByMethod[
            method
          ] || {};

      let next;

      if (unit) {
        next = {
          ...existing,
          [field]: {
            ...(existing[field] || {}),
            [unit]: input.value
          }
        };
      } else {
        next = {
          ...existing,
          [field]: input.value
        };
      }

      state = core.updateAreaInputs(
        state,
        areaId,
        {
          measurementInputsByMethod: {
            [method]: next
          }
        }
      );
    }

    function updateMaterial(
      areaId,
      value
    ) {
      if (!value) {
        state = core.updateAreaInputs(
          state,
          areaId,
          {
            materialId: null
          }
        );
        return;
      }

      const material =
        config.materials.find(
          function (candidate) {
            return candidate.id === value;
          }
        );

      if (!material) {
        return;
      }

      const eligibility =
        rules.evaluateMaterialSelectionEligibility(
          material
        );

      if (!eligibility.selectable) {
        return;
      }

      state = core.updateAreaInputs(
        state,
        areaId,
        {
          materialId: value
        }
      );
    }

    function updateDepth(
      areaId,
      value
    ) {
      state = core.updateAreaInputs(
        state,
        areaId,
        {
          depthInches: value
        }
      );
    }

    function updateMultiplier(
      areaId,
      value
    ) {
      const normalized =
        core.normalizeIdenticalAreaMultiplier(
          value
        );

      if (
        normalized.status ===
        core.MEASUREMENT_STATUS.VALID
      ) {
        state = core.updateAreaInputs(
          state,
          areaId,
          {
            identicalAreaMultiplier:
              normalized.multiplier
          }
        );
        return;
      }

      state = core.updateAreaInputs(
        state,
        areaId,
        {
          identicalAreaMultiplier:
            value
        }
      );
    }

    function rerender() {
      renderAreaList();
      renderSummary();
      saveRecovery();
    }

    function rerenderArea(areaId, preservePartControls) {
      const card = areaList.querySelector(
        '[data-ms-area-card][data-ms-area-id="' +
          areaId +
          '"]'
      );
      const area = getArea(areaId);

      if (!card || !area) {
        rerender();
        return;
      }

      const index = state.areas.findIndex(
        function (candidate) {
          return candidate.id === areaId;
        }
      );

      renderAreaCard(
        card,
        area,
        index,
        preservePartControls
      );
      renderSummary();
      saveRecovery();
    }

    function handleMeasurementGuidanceKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) ||
          event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const button = event.target.closest('button');
      if (!button) return;
      const group = button.closest('[data-ms-guidance-step]');
      if (!group) return;
      const step = group.getAttribute('data-ms-guidance-step');
      if (!['square-footage', 'box', 'around', 'around-round', 'round-area', 'triangle', 'same-size'].includes(step)) return;
      const selector = 'button[data-ms-guidance-' + step + ']';
      if (!button.matches(selector)) return;

      const answers = Array.from(group.querySelectorAll(selector))
        .filter(answer => !answer.disabled && !answer.closest('[hidden]'));
      const index = answers.indexOf(button);
      if (index < 0 || answers.length < 2) return;
      event.preventDefault();
      // Stop at either end. Focus navigation never selects or commits an answer.
      const next = answers[index + (event.key === 'ArrowRight' ? 1 : -1)];
      if (next) next.focus();
    }

    function handleMeasurementGuidanceClick(event) {
      const button = event.target.closest(
        '[data-ms-guidance-same-size], [data-ms-guidance-change], [data-ms-guidance-box], [data-ms-guidance-around], ' +
        '[data-ms-guidance-around-round], [data-ms-guidance-round-area], ' +
        '[data-ms-guidance-triangle], [data-ms-triangle-smaller-parts], ' +
        '[data-ms-guidance-square-footage], ' +
        '[data-ms-guidance-around-square-footage]'
      );

      if (!button) {
        return;
      }

      const card = button.closest(
        '[data-ms-area-card]'
      );

      if (!card) {
        return;
      }

      const areaId =
        card.getAttribute(
          'data-ms-area-id'
        );

      if (!areaId) {
        return;
      }

      const editing = button.matches('[data-ms-guidance-change]')
        ? button.getAttribute('data-ms-guidance-change') : null;
      if (editing) {
        card.msGuidanceEditing = editing;
        renderMeasurementGuidance(card, getArea(areaId));
        renderAreaWorkflow(card, getArea(areaId), deriveAreaBundle(getArea(areaId)));
        const group = button.closest('[data-ms-guidance-step]');
        const answer = group.querySelector('[aria-pressed="true"]') || group.querySelector('[data-ms-guidance-active] button');
        if (answer) answer.focus();
        return;
      }
      card.msGuidanceEditing = null;
      if (button.matches('[data-ms-triangle-smaller-parts]')) {
        handleMeasurementGuidanceInput(card, areaId, {
          value: 'no', matches: selector => selector === '[data-ms-guidance-triangle]'
        });
        const next = card.querySelector('[data-ms-guidance-step="smaller-parts"] button');
        if (next) next.focus();
        renderStartOverVisibility();
        return;
      }
      if (button.matches('[data-ms-guidance-same-size]')) {
        const area = getArea(areaId);
        const patch = {sameSizeAreas: button.value};
        if (area.inputs.sameSizeAreas !== button.value && button.value === 'no') {
          patch.identicalAreaMultiplier = 1;
        }
        state = core.updateAreaInputs(state, areaId, patch);
        rerenderArea(areaId);
        return;
      }
      if (handleMeasurementGuidanceInput(card, areaId, button)) {
        renderStartOverVisibility();
        return;
      }

      if (
        button.matches(
          '[data-ms-guidance-square-footage]'
        )
      ) {
        const choice =
          button.getAttribute(
            'data-ms-guidance-square-footage'
          );

        if (getArea(areaId).inputs.squareFootageChoice === choice) {
          rerenderArea(areaId);
          return;
        }
        card.msGuidanceAnswers = {};
        state = core.updateAreaInputs(state, areaId, {squareFootageChoice: choice, guidanceAnswers: {}});
        setPendingMeasurementGuidance(areaId, null, true);

        if (choice === 'known') {
          resolveGuidedMeasurementMethod(
            card,
            areaId,
            'known-square-footage'
          );

          return;
        }

        if (choice === 'unknown') {
          setPendingMeasurementGuidance(areaId, 'box');
        }

        return;
      }

      if (
        button.getAttribute(
          'data-ms-guidance-around-square-footage'
        ) === 'known'
      ) {
        const answers = {...(getArea(areaId).inputs.guidanceAnswers || card.msGuidanceAnswers || {}), 'around-recovery': 'known'};
        card.msGuidanceAnswers = answers;
        state = core.updateAreaInputs(state, areaId, {guidanceAnswers: answers});
        resolveGuidedMeasurementMethod(
          card,
          areaId,
          'known-square-footage'
        );
      }
    }

    function handleAreaInput(event) {
      const input = event.target;
      const card = input.closest(
        '[data-ms-area-card]'
      );

      if (!card) {
        return;
      }

      const areaId =
        card.getAttribute(
          'data-ms-area-id'
        );

      if (!areaId) {
        return;
      }

      if (input.matches('[data-ms-plan-input]')) {
        const key = input.getAttribute('data-ms-plan-input');
        card.msPlanEditing = key === 'customSpacingFeet' ? 'spacing' : key;
        updatePlanDecision(areaId, key, input.value);
        rerenderArea(areaId);
        return;
      }

      if (
        input.matches(
          '[data-ms-area-name]'
        )
      ) {
        updateAreaName(
          areaId,
          input.value
        );
        rerenderArea(areaId);
        return;
      }

      if (
        handleSmallerPartInput(
          card,
          areaId,
          input
        )
      ) {
        return;
      }

      if (
        input.matches(
          '[data-ms-geometry-input]'
        )
      ) {
        updateGeometryInput(
          areaId,
          input
        );
        rerenderArea(areaId);
        return;
      }

      if (
        input.matches(
          '[data-ms-material]'
        )
      ) {
        updateMaterial(
          areaId,
          input.value
        );
        rerenderArea(areaId);
        return;
      }

      if (
        input.matches(
          '[data-ms-depth-choice]'
        )
      ) {
        if (input.value === 'custom') {
          card.setAttribute(
            'data-ms-custom-depth-active',
            'true'
          );
          showCustomDepthEntry(card);
          return;
        }

        card.removeAttribute(
          'data-ms-custom-depth-active'
        );
        updateDepth(
          areaId,
          input.value
        );
        rerenderArea(areaId);
        return;
      }

      if (
        input.matches(
          '[data-ms-depth-inches]'
        )
      ) {
        updateDepth(
          areaId,
          input.value
        );
        rerenderArea(areaId);
        return;
      }

      if (
        input.matches(
          '[data-ms-area-multiplier]'
        )
      ) {
        updateMultiplier(
          areaId,
          input.value
        );
        rerenderArea(areaId);
        return;
      }
    }

    projectNameInput.addEventListener(
      'input',
      function () {
        state = core.updateProjectName(
          state,
          projectNameInput.value
        );
        renderStartOverVisibility();
        const manual = root.querySelector('[data-ms-manual-copy]');
        if (manual) manual.hidden = true;
        saveRecovery();
      }
    );

    const copyButton = root.querySelector('[data-ms-copy-list]');
    const printButton = root.querySelector('[data-ms-print-list]');
    let copyStatusTimer = null;
    if (copyButton) copyButton.addEventListener('click', async function () {
      const content = materialListText(buildProjectMaterialList());
      const status = root.querySelector('[data-ms-copy-status]');
      const fallback = root.querySelector('[data-ms-manual-copy]');
      try {
        if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(content);
        if (fallback) fallback.hidden = true;
        if (status) status.textContent = 'Project Material List copied.';
        if (copyStatusTimer) clearTimeout(copyStatusTimer);
        copyStatusTimer = setTimeout(function () { if (status) status.textContent = ''; }, 4000);
      } catch (_) {
        const text = root.querySelector('[data-ms-manual-copy-text]');
        if (text) text.value = content;
        if (fallback) fallback.hidden = false;
        if (status) status.textContent = '';
      }
    });
    if (printButton) printButton.addEventListener('click', function () {
      renderPrintList(buildProjectMaterialList());
      window.print();
    });

    const startOverButton = root.querySelector('[data-ms-start-over]');
    const startOverConfirmation = root.querySelector(
      '[data-ms-start-over-confirmation]'
    );
    const confirmStartOverButton = root.querySelector('[data-ms-confirm-start-over]');
    const cancelStartOverButton = root.querySelector('[data-ms-cancel-start-over]');

    if (
      startOverButton && startOverConfirmation &&
      confirmStartOverButton && cancelStartOverButton
    ) {
      startOverButton.addEventListener('click', function () {
        startOverConfirmation.hidden = false;
        cancelStartOverButton.focus();
      });

      cancelStartOverButton.addEventListener('click', function () {
        startOverConfirmation.hidden = true;
        startOverButton.focus();
      });

      confirmStartOverButton.addEventListener('click', function () {
        if (startOverConfirmation.hidden) {
          return;
        }

        state = core.createProjectState();
        lastSavedSignature = null;
        pendingDeleteAreaId = null;
        expandedAreaIds.clear();
        revision = null;
        smallerPartSequence = 0;
        projectNameInput.value = state.project.name;
        startOverConfirmation.hidden = true;
        const restoredNotice = root.querySelector('[data-ms-restored-notice]');
        if (restoredNotice) restoredNotice.hidden = true;
        rerender();
        if (recovery) recovery.clear();
        projectNameInput.focus();
      });
    }

    areaList.addEventListener('click', handlePlanClick);

    areaList.addEventListener(
      'input',
      handleAreaInput
    );

    areaList.addEventListener(
      'change',
      handleAreaInput
    );
    areaList.addEventListener(
      'click',
      handleMeasurementGuidanceClick
    );
    areaList.addEventListener(
      'keydown',
      handleMeasurementGuidanceKeydown
    );

    addAreaButton.addEventListener(
      'click',
      function () {
        if (revision) return;
        state = core.addArea(state);
        pendingDeleteAreaId = null;
        rerender();
      }
    );

    areaList.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest(
          'button'
        );

        if (!button) {
          return;
        }

        const card = button.closest(
          '[data-ms-area-card]'
        );

        if (!card) {
          return;
        }

        const areaId =
          card.getAttribute(
            'data-ms-area-id'
          );

        if (!areaId) {
          return;
        }

        if (
          button.matches(
            '[data-ms-measure-first-part]'
          )
        ) {
          beginSmallerPartsMeasurement(
            areaId
          );
          return;
        }

        if (button.matches('[data-ms-remove-smaller-part]')) {
          const removedFocusedControl = document.activeElement === button;
          removeSmallerMeasurementPart(areaId, button.getAttribute('data-ms-remove-smaller-part'));
          if (removedFocusedControl) {
            // The focused node was removed; return to a surviving local control.
            const target = card.querySelector('[data-ms-done-smaller-parts]') ||
              card.querySelector('[data-ms-smaller-part-method]');
            if (target) target.focus();
          }
          return;
        }

        if (
          button.matches(
            '[data-ms-add-smaller-part]'
          )
        ) {
          addSmallerMeasurementPart(
            areaId
          );
          return;
        }

        if (
          button.matches(
            '[data-ms-done-smaller-parts]'
          )
        ) {
          const area = getArea(areaId);

          if (
            area &&
            smallerPartsAreValid(area)
          ) {
            updateSmallerPartsArea(
              areaId,
              {
                measurementComposition:
                  'smaller-parts',
                measurementPartsDone:
                  true
              }
            );
          }

          return;
        }

        if (
          button.matches(
            '[data-ms-toggle-area]'
          )
        ) {
          if (expandedAreaIds.has(areaId)) {
            expandedAreaIds.delete(areaId);
          } else {
            expandedAreaIds.add(areaId);
          }
          const details = card.querySelector('[data-ms-area-details]');
          if (details) details.hidden = !expandedAreaIds.has(areaId);
          button.setAttribute('aria-expanded', expandedAreaIds.has(areaId) ? 'true' : 'false');
          return;
        }

        if (button.matches('[data-ms-change-area]')) {
          if (revision) return;
          const area = getArea(areaId);
          if (deriveAreaBundle(area).contribution.status !== core.MEASUREMENT_STATUS.VALID) return;
          revision = {areaId, committedArea: JSON.parse(JSON.stringify(area))};
          pendingDeleteAreaId = null;
          expandedAreaIds.delete(areaId);
          addAreaButton.disabled = true;
          rerenderArea(areaId);
          const currentCard = areaList.querySelector('[data-ms-area-card][data-ms-area-id="' + areaId + '"]');
          const first = currentCard && currentCard.querySelector('[data-ms-area-name]');
          if (first) first.focus();
          return;
        }

        if (button.matches('[data-ms-cancel-revision]')) {
          if (!revision || revision.areaId !== areaId) return;
          replaceArea(areaId, revision.committedArea);
          revision = null;
          addAreaButton.disabled = false;
          rerenderArea(areaId);
          const change = card.querySelector('[data-ms-change-area]');
          if (change) change.focus();
          return;
        }

        if (button.matches('[data-ms-save-area-changes]')) {
          if (!revision || revision.areaId !== areaId) return;
          if (!revisionIsComplete(getArea(areaId))) return;
          revision = null;
          addAreaButton.disabled = false;
          rerenderArea(areaId);
          const change = card.querySelector('[data-ms-change-area]');
          if (change) change.focus();
          return;
        }

        if (
          button.matches(
            '[data-ms-delete-area]'
          )
        ) {
          if (revision) return;
          pendingDeleteAreaId =
            areaId;
          rerender();
          return;
        }

        if (
          button.matches(
            '[data-ms-confirm-delete]'
          )
        ) {
          if (
            pendingDeleteAreaId === areaId && !revision
          ) {
            state = core.removeArea(
              state,
              areaId
            );
            pendingDeleteAreaId = null;
            expandedAreaIds.delete(
              areaId
            );
            rerender();
          }

          return;
        }

        if (
          button.matches(
            '[data-ms-cancel-delete]'
          )
        ) {
          pendingDeleteAreaId = null;
          rerender();
          return;
        }
      }
    );

    root.setAttribute(
      'data-ms-project-planner-config-revision',
      config.revision
    );

    renderAreaList();
    renderSummary();

    projectNameInput.value =
      state.project.name;

    if (window.MSProjectPlannerAccessUI) {
      window.MSProjectPlannerAccessUI.mount(root, {beforeRequestSubmit: function () {
        return saveRecovery(true);
      }, restoreView: function () {
        projectNameInput.value = state.project.name;
        renderAreaList();
        renderSummary();
      }});
    } else {
      // Missing access assets cannot silently enable protected interactions.
      root.setAttribute('inert', '');
    }

    root.setAttribute(
      'data-ms-project-planner-status',
      'ready'
    );
  }

  function initializeAllPlanners() {
    document
      .querySelectorAll(
        '[data-ms-project-planner]'
      )
      .forEach(
        initializePlanner
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllPlanners,
      { once: true }
    );
  } else {
    initializeAllPlanners();
  }
})();
